import { Box, Text } from '@fundamentals'
import React from 'react'
import {
  MultipleChoiceQuestionRules,
  QuestionTypeComponentType,
} from '@core/components/templateAnswersForm'
import { MultipleChoice } from '@common/MultipleChoice'
import produce from 'immer'
import { TextField } from '@common/material'
import { Autocomplete } from '@common/material/Autocomplete'
import { localizedStrings } from '@core/strings'
import { IPermitMultipleChoiceAnswer } from '@core/entities/permit/PermitMultipleChoiceAnswer'

const MultipleChoiceQuestion: QuestionTypeComponentType = ({
  question,
  inputProps: { value = question, onChange, onBlur, ...inputProps } = {},
  error,
  readOnly,
}) => {
  const renderAdditionalChoiceSubmissionText = (
    index: number,
    questionValue?: string,
  ) => {
    const { requiresSubmissionText, selected } = value.answers[index]
    if (!requiresSubmissionText || !selected) return null
    return (
      <Box mt={2} width={'100%'}>
        {readOnly ? (
          <Text>{value.answers[index].submissionText}</Text>
        ) : (
          <TextField
            value={value.answers[index].submissionText}
            onChange={(e) => {
              const newValue = produce(value, (draft) => {
                draft.answers[index].submissionText = e.target.value
              })
              onChange(newValue)
            }}
            placeholder={
              !questionValue && localizedStrings.additionalTextForAnswer()
            }
            label={
              questionValue &&
              localizedStrings.additionalTextForAnswer(questionValue)
            }
            onBlur={onBlur}
            minRows={3}
            multiline
            fullWidth
            {...inputProps}
          />
        )}
      </Box>
    )
  }

  if (onChange && !readOnly) {
    if (value.answers.length > 5) {
      const multiple = value.minAnswers > 1 || value.maxAnswers > 1
      const inputValue = multiple
        ? value.answers.filter((answer) => answer.selected)
        : value.answers.find((answer) => answer.selected)

      return (
        <Box>
          <Autocomplete
            multiple={multiple}
            value={inputValue}
            options={value?.answers}
            onChange={(_, selectedOption) => {
              const newValue = produce(value, (draft) => {
                draft.answers.forEach((answer) => {
                  answer.selected = multiple
                    ? (selectedOption as IPermitMultipleChoiceAnswer[])
                        .map(({ id }) => id)
                        .includes(answer.id)
                    : (selectedOption as IPermitMultipleChoiceAnswer).id ===
                      answer.id
                })
              })
              onChange(newValue)
            }}
            isOptionEqualToValue={(
              option: IPermitMultipleChoiceAnswer,
              value: IPermitMultipleChoiceAnswer,
            ) => option.id === value.id}
            getOptionLabel={(option: IPermitMultipleChoiceAnswer) =>
              option.value
            }
            freeSolo={false}
            disableClearable
            renderInput={(params) => {
              const hasAnswer = value.answers.find((a) => a.selected)
              return (
                <TextField
                  placeholder={
                    !hasAnswer &&
                    localizedStrings.selectOptions(
                      value.minAnswers,
                      value.maxAnswers,
                    )
                  }
                  multiline
                  {...params}
                />
              )
            }}
          />
          {value.answers.map((answer, index) => {
            if (answer.selected && answer.requiresSubmissionText) {
              return (
                <Box key={index}>
                  {renderAdditionalChoiceSubmissionText(index, answer.value)}
                </Box>
              )
            }
          })}
        </Box>
      )
    }

    return (
      <MultipleChoice
        options={value.answers.map((answer, index) => {
          return {
            label: answer.value,
            value: answer.id,
            footer: renderAdditionalChoiceSubmissionText(index),
          }
        })}
        selected={value.answers
          .filter((answer) => answer.selected)
          .map(({ id }) => id)}
        onSelect={(selected) => {
          const newValue = produce(value, (draft) => {
            draft.answers.forEach((answer) => {
              if (selected.includes(answer.id)) answer.selected = true
              else answer.selected = false
            })
          })
          onChange(newValue)
          onBlur()
        }}
        maxSelect={question.maxAnswers}
        showCheckbox
        multiSelect
        dataTest={`${question.id}-MULTIPLE_CHOICE`}
      />
    )
  }

  return (
    <Box data-test={`${question.id}-MULTIPLE_CHOICE`}>
      {question?.answers.map((answer, index) => {
        if (!answer.selected) return null
        return (
          <Text key={index}>
            {`${answer.value} ${
              answer.requiresSubmissionText ? `(${answer.submissionText})` : ''
            }`}
          </Text>
        )
      })}
    </Box>
  )
}

MultipleChoiceQuestion.rules = MultipleChoiceQuestionRules

export { MultipleChoiceQuestion }
